import { FileExcelOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Divider } from "antd";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SearchFilterCard from "../../components/general/filter-card";
import PageHeader from "../../components/general/layout/page-header";
import RefreshBtn from "../../components/general/table-components/actions/refresh-btn";
import AppContext from "../../context/app/context";
import { useIsMount } from "../../utils/hooks/is-mount";
import Can from "../../utils/rbac/can";
import { CAN_VIEW_HOME_PAGE } from "../../utils/rbac/permissions";
import {Permissions} from "../../utils/Permissions/Permissions";
import styles from "./style.module.scss";
import DeliveryWorkerContext from "../../context/delivery-worker/context";
import DeliveryWorkerTable from "../../components/delivery-worker/delivery-worker-table";
import DeliveryWorkerContextProvider from "../../context/delivery-worker/provider";
import DeliveryWorkerFilter from "../../components/delivery-worker/delivery-worker-filter";
import ExportToExcelModal from "../../components/general/excel/export-to-excel-modal";
import {
  IExportDeliveryWorkerExcel,
  columnsExportDeliveryWorkerExcel,
} from "../../models/delivery-workers/request";
import TemplateExportExcel from "../../components/general/excel/expor-excel";
import { addSearchToURL, getFiltersFromURL, getKeywordFromURL } from "../../utils/helpers/url";
import { debounce } from "lodash";
interface IProps {
  children?: React.ReactNode;
}

const DeliveryWorker: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const { direction } = useContext(AppContext);
  const navigate = useNavigate();
  const [exportModalVisible, setExportModalVisible] = useState(false);

  const [search, setSearch] = useState<string | undefined>(getKeywordFromURL());

  const { actions, loading, query } = useContext(DeliveryWorkerContext);

  useEffect(() => {
    const filters = getFiltersFromURL();
    actions.setQuery({
      page: 1,
      perPage: 10,
      filters: filters,
      keyword: search
    });
  }, []);

  const debouncedSearch = debounce((value: string | undefined) => {
    addSearchToURL(value);
    actions.setSearch(value);
  }, 300);

  useEffect(() => {
    debouncedSearch(search);
    return () => {
      debouncedSearch.cancel();
    };
  }, [search]);

  const isMounted = useIsMount();
  useEffect(() => {
    !isMounted && actions.getData();
  }, [query, direction]);
  return (
    <div dir={direction} className={styles.container}>
      <PageHeader
        title={t("delivery_workers")}
        subTitle={t("delivery_workers_management")}
        extra={[
          <RefreshBtn
            key={0}
            loading={loading.includes("list")}
            onClick={() => actions.getData()}
          >
            {t("refresh")}
          </RefreshBtn>,
          <Divider key={1} type="vertical" />,
          <Button
            type="primary"
            onClick={() => {
              setExportModalVisible(true);
            }}
            icon={<FileExcelOutlined />}
          >
            {t("export_to_excel")}
          </Button>,
          <Can key={2} perform={Permissions.CAN_CREATE_DRIVER}>
            <Button
              type="primary"
              onClick={() => {
                navigate(`/delivery-worker/create`);
              }}
              icon={<PlusOutlined />}
            >
              {t("add_delivery_worker")}
            </Button>
          </Can>,
        ]}
      />
      <ExportToExcelModal
        id="export-category"
        title={t("export_to_excel")}
        visible={exportModalVisible}
        toggleVisible={setExportModalVisible}
        width="50vw"
        defaultValues={
          {
            id: false,
            firstName: false,
            lastName: false,
            status: false,
            city: false,
            mobile: false,
            createdAt: false,
            statusWorker: false,
          } as IExportDeliveryWorkerExcel
        }
        loading={loading.includes("create")}
        onSubmit={async (columnsNames) => {
          // await a(columnsNames);
          await actions.exportExcel(columnsNames);
          setExportModalVisible(false);
        }}
      >
        <TemplateExportExcel names={columnsExportDeliveryWorkerExcel} />
      </ExportToExcelModal>
      <SearchFilterCard
        formId="delivery-worker-filter"
        searchValue={search}
        setSearchValue={setSearch}
        onReset={() => {
          const searchParams = new URLSearchParams(location.search);
          searchParams.delete("keyword");
          Object.keys(getFiltersFromURL()).forEach((key) =>
            searchParams.delete(key)
          );
          navigate(`${location.pathname}`, { replace: true });
          actions.setQuery({
            page: 1,
            perPage: query?.perPage,
            keyword: undefined,
            filters: [],
          });
          const form = document.getElementById("delivery-worker-filter");
          if (form) {
            form.dispatchEvent(new Event("reset", { cancelable: true, bubbles: true }));
          }
        }}
        resetLoading={loading.includes("list")}
        applyLoading={loading.includes("list")}
      >
        <DeliveryWorkerFilter />
      </SearchFilterCard>

      <DeliveryWorkerTable />
    </div>
  );
};

const DeliveryWorkersPage = () => {
  return (
    <DeliveryWorkerContextProvider>
      <DeliveryWorker />
    </DeliveryWorkerContextProvider>
  );
};
export default DeliveryWorkersPage;
